import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, IconButton, Tooltip, Checkbox, Chip, Switch,
  Dialog, DialogContent, TextField, MenuItem, CircularProgress,
  Snackbar, Alert, alpha,
} from '@mui/material';
import {
  Add as AddIcon, Refresh as RefreshIcon, Edit as EditIcon,
  DeleteOutline as DeleteIcon, Close as CloseIcon, Smartphone as PhoneIcon,
  Bolt as BoltIcon, CheckCircleOutline as ActivateIcon, DoNotDisturbOn as DisableIcon,
} from '@mui/icons-material';
import { designTokens as t } from '../theme';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// Doit rester cohérent avec barcelonaVersion/barcelonaVersionCode côté backend.
const BARCELONA_VERSION = '435.0.0.36.76';
const BARCELONA_CODE = '1001219512';

const NETWORK_TYPES = ['WIFI', '4G', '5G'];
const TLS_PROFILES = ['okhttp4_android_13', 'okhttp4_android_12', 'okhttp4_android_11'];

const EMPTY = {
  name: '', manufacturer: 'samsung', deviceModel: '', product: '', chipset: 'qcom',
  dpi: '420dpi', resolution: '1080x2400', androidVersion: '14', androidApi: '34',
  tlsProfile: 'okhttp4_android_13', networkType: 'WIFI', isActive: true,
};

function buildUA(p) {
  const api = p.androidApi || p.androidVersion || '';
  return `Barcelona ${BARCELONA_VERSION} Android (${api}/${p.androidVersion}; ${p.dpi}; ${p.resolution}; ${p.manufacturer}; ${p.deviceModel}; ${p.product}; ${p.chipset}; en_US; ${BARCELONA_CODE})`;
}

export default function Profiles() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState(new Set());
  const [dialog, setDialog] = useState({ open: false, editingName: null, form: EMPTY });
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: '', sev: 'success' });
  const notify = (msg, sev = 'success') => setToast({ open: true, msg, sev });

  const fetchProfiles = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/mobile-profiles`);
      setProfiles(Array.isArray(data) ? data : []);
    } catch (e) {
      notify(`Erreur de chargement: ${e.response?.data?.error || e.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  const activeCount = useMemo(() => profiles.filter((p) => p.isActive).length, [profiles]);

  const openAdd = () => setDialog({ open: true, editingName: null, form: EMPTY });
  const openEdit = (p) => setDialog({ open: true, editingName: p.name, form: { ...EMPTY, ...p } });
  const closeDialog = () => setDialog((d) => ({ ...d, open: false }));
  const setField = (k, v) => setDialog((d) => ({ ...d, form: { ...d.form, [k]: v } }));

  const handleSave = async () => {
    const f = dialog.form;
    if (!f.name.trim() || !f.deviceModel.trim()) { notify('Nom et modèle requis', 'error'); return; }
    setBusy(true);
    try {
      if (dialog.editingName) {
        await axios.put(`${API}/api/mobile-profiles/${encodeURIComponent(dialog.editingName)}`, f);
        notify('Profil mis à jour');
      } else {
        await axios.post(`${API}/api/mobile-profiles`, f);
        notify('Profil ajouté');
      }
      closeDialog();
      fetchProfiles();
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      await axios.put(`${API}/api/mobile-profiles/${encodeURIComponent(p.name)}`, { ...p, isActive: !p.isActive });
      setProfiles((prev) => prev.map((x) => (x.name === p.name ? { ...x, isActive: !x.isActive } : x)));
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
    }
  };

  const bulkSetActive = async (active) => {
    const names = [...selection];
    if (!names.length) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/mobile-profiles/bulk-active`, { names, active });
      setProfiles((prev) => prev.map((x) => (selection.has(x.name) ? { ...x, isActive: active } : x)));
      setSelection(new Set());
      notify(active ? 'Profil(s) activé(s)' : 'Profil(s) désactivé(s)');
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const deleteNames = async (names) => {
    if (!names.length) return;
    if (!window.confirm(`Supprimer ${names.length} profil(s) ?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/api/mobile-profiles`, { data: names });
      setSelection(new Set());
      notify('Supprimé(s)');
      fetchProfiles();
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const toggleSel = (name) => setSelection((prev) => {
    const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n;
  });

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, letterSpacing: '-0.02em' }}>Profiles mobiles</Typography>
          <Typography sx={{ fontSize: '0.88rem', color: t.muted }}>Pool d'appareils Android assignés aux comptes</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {selection.size > 0 && (
            <>
              <Button variant="outlined" startIcon={<ActivateIcon />} onClick={() => bulkSetActive(true)} disabled={busy}
                sx={{ textTransform: 'none', fontWeight: 600, color: t.success, borderColor: alpha(t.success, 0.5), '&:hover': { borderColor: t.success, bgcolor: alpha(t.success, 0.08) } }}>
                Activer ({selection.size})
              </Button>
              <Button variant="outlined" startIcon={<DisableIcon />} onClick={() => bulkSetActive(false)} disabled={busy}
                sx={{ textTransform: 'none', fontWeight: 600, color: t.warning, borderColor: alpha(t.warning, 0.5), '&:hover': { borderColor: t.warning, bgcolor: alpha(t.warning, 0.08) } }}>
                Désactiver ({selection.size})
              </Button>
              <Button color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={() => deleteNames([...selection])} disabled={busy}
                sx={{ textTransform: 'none', fontWeight: 600 }}>
                Supprimer ({selection.size})
              </Button>
            </>
          )}
          {profiles.length > 0 && (
            <Button variant="outlined" onClick={() => setSelection(selection.size === profiles.length ? new Set() : new Set(profiles.map((p) => p.name)))}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              {selection.size === profiles.length ? 'Tout désélectionner' : 'Tout sélectionner'}
            </Button>
          )}
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchProfiles} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Rafraîchir
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}
            sx={{ textTransform: 'none', fontWeight: 600, bgcolor: t.primary, '&:hover': { bgcolor: t.primaryHover }, boxShadow: 'none' }}>
            Ajouter un appareil
          </Button>
        </Box>
      </Box>

      {/* Info banner */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1.5, mb: 2.5, borderRadius: 2, border: `1px solid ${alpha(t.primary, 0.3)}`, bgcolor: alpha(t.primary, 0.08) }}>
        <BoltIcon sx={{ fontSize: 18, color: t.primary }} />
        <Typography sx={{ fontSize: '0.82rem', color: t.muted }}>
          Chaque nouveau compte tire au hasard un appareil <b style={{ color: t.foreground }}>actif</b> de ce pool, puis le garde à vie. Il sert à la validation, aux followers et à la publication. <b style={{ color: t.foreground }}>{activeCount}</b> appareil(s) actif(s).
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: t.primary }} /></Box>
      ) : profiles.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, color: t.muted }}>
          <PhoneIcon sx={{ fontSize: 40, color: t.mutedFaint, mb: 1 }} />
          <Typography>Aucun appareil. Ajoutes-en un.</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 1.5 }}>
          {profiles.map((p) => {
            const sel = selection.has(p.name);
            return (
              <Box key={p.name} sx={{ border: `1px solid ${sel ? alpha(t.primary, 0.5) : t.border}`, borderRadius: 2.5, bgcolor: t.card, p: 2, position: 'relative',
                opacity: p.isActive ? 1 : 0.55, transition: 'border-color .15s' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                  <Checkbox size="small" checked={sel} onChange={() => toggleSel(p.name)} sx={{ p: 0.25, mt: -0.25 }} />
                  <Box sx={{ width: 34, height: 34, borderRadius: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(t.primary, 0.12), color: t.primary, flexShrink: 0 }}>
                    <PhoneIcon sx={{ fontSize: 19 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</Typography>
                    <Typography sx={{ fontSize: '0.74rem', color: t.mutedFaint }}>{p.deviceModel} · Android {p.androidVersion}</Typography>
                  </Box>
                  <Tooltip title={p.isActive ? 'Actif — utilisé par les comptes' : 'Inactif — ignoré'}>
                    <Switch size="small" checked={!!p.isActive} onChange={() => toggleActive(p)} />
                  </Tooltip>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.25 }}>
                  <Chip label={p.chipset || '—'} size="small" sx={{ height: 20, fontSize: '0.68rem', bgcolor: t.background, color: t.muted, border: `1px solid ${t.border}` }} />
                  <Chip label={p.resolution || '—'} size="small" sx={{ height: 20, fontSize: '0.68rem', bgcolor: t.background, color: t.muted, border: `1px solid ${t.border}` }} />
                  <Chip label={p.networkType || 'WIFI'} size="small" sx={{ height: 20, fontSize: '0.68rem', bgcolor: t.background, color: t.muted, border: `1px solid ${t.border}` }} />
                  <Chip label={p.tlsProfile || 'okhttp'} size="small" sx={{ height: 20, fontSize: '0.68rem', bgcolor: alpha(t.success, 0.12), color: t.success, border: `1px solid ${alpha(t.success, 0.3)}` }} />
                </Box>

                <Typography sx={{ fontSize: '0.66rem', color: t.mutedFaint, fontFamily: 'monospace', lineHeight: 1.4, wordBreak: 'break-all',
                  bgcolor: t.background, border: `1px solid ${t.border}`, borderRadius: 1, p: 0.75, mb: 1 }}>
                  {buildUA(p)}
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                  <Tooltip title="Éditer"><IconButton size="small" onClick={() => openEdit(p)} sx={{ color: t.muted }}><EditIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                  <Tooltip title="Supprimer"><IconButton size="small" onClick={() => deleteNames([p.name])} sx={{ color: '#f87171' }}><DeleteIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: t.card, border: `1px solid ${t.border}`, borderRadius: 3, backgroundImage: 'none' } }}>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2.5, pt: 2.5, pb: 1.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(t.primary, 0.14), color: t.primary }}>
              <PhoneIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>{dialog.editingName ? "Éditer l'appareil" : 'Nouvel appareil'}</Typography>
              <Typography sx={{ fontSize: '0.78rem', color: t.muted }}>Caractéristiques utilisées pour générer le User-Agent</Typography>
            </Box>
            <IconButton size="small" onClick={closeDialog} sx={{ color: t.muted }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
          </Box>

          <Box sx={{ px: 2.5, pb: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
            <Field label="Nom" value={dialog.form.name} onChange={(v) => setField('name', v)} full />
            <Field label="Fabricant" value={dialog.form.manufacturer} onChange={(v) => setField('manufacturer', v)} />
            <Field label="Modèle (DeviceModel)" value={dialog.form.deviceModel} onChange={(v) => setField('deviceModel', v)} />
            <Field label="Produit (product)" value={dialog.form.product} onChange={(v) => setField('product', v)} />
            <Field label="Chipset" value={dialog.form.chipset} onChange={(v) => setField('chipset', v)} />
            <Field label="Android (version)" value={dialog.form.androidVersion} onChange={(v) => setField('androidVersion', v)} />
            <Field label="API level" value={dialog.form.androidApi} onChange={(v) => setField('androidApi', v)} />
            <Field label="DPI" value={dialog.form.dpi} onChange={(v) => setField('dpi', v)} />
            <Field label="Résolution" value={dialog.form.resolution} onChange={(v) => setField('resolution', v)} />
            <Field label="Réseau" value={dialog.form.networkType} onChange={(v) => setField('networkType', v)} select options={NETWORK_TYPES} />
            <Field label="Profil TLS" value={dialog.form.tlsProfile} onChange={(v) => setField('tlsProfile', v)} select options={TLS_PROFILES} />
          </Box>

          <Box sx={{ px: 2.5, pb: 0.5 }}>
            <Typography sx={{ fontSize: '0.68rem', color: t.mutedFaint, mb: 0.5, fontWeight: 600 }}>APERÇU DU USER-AGENT</Typography>
            <Typography sx={{ fontSize: '0.68rem', color: t.muted, fontFamily: 'monospace', wordBreak: 'break-all', bgcolor: t.background, border: `1px solid ${t.border}`, borderRadius: 1, p: 1 }}>
              {buildUA(dialog.form)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Switch size="small" checked={!!dialog.form.isActive} onChange={(e) => setField('isActive', e.target.checked)} />
              <Typography sx={{ fontSize: '0.82rem', color: t.muted }}>Actif</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={closeDialog} sx={{ textTransform: 'none', color: t.muted, fontWeight: 600 }}>Annuler</Button>
              <Button onClick={handleSave} disabled={busy} variant="contained"
                startIcon={busy ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : null}
                sx={{ textTransform: 'none', fontWeight: 600, bgcolor: t.primary, '&:hover': { bgcolor: t.primaryHover }, boxShadow: 'none' }}>
                {dialog.editingName ? 'Enregistrer' : 'Ajouter'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={3500} onClose={() => setToast((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={toast.sev} variant="filled" onClose={() => setToast((s) => ({ ...s, open: false }))}>{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
}

function Field({ label, value, onChange, select, options, full }) {
  return (
    <TextField
      label={label} value={value || ''} onChange={(e) => onChange(e.target.value)}
      select={select} size="small" fullWidth
      sx={{ gridColumn: full ? '1 / -1' : 'auto', '& .MuiOutlinedInput-root': { bgcolor: t.background } }}
    >
      {select && options.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
    </TextField>
  );
}
